import React, { useEffect, useState } from 'react';
import './Dashboard.scss';
import { Link, useNavigate } from 'react-router-dom';
import { MODULES_API_MAP, httpService } from '@Src/services/httpService';
import { GLOBAL_API_ROUTES } from '@Src/services/globalApiRoutes';
import { IAPIResponse, IToken } from '@Src/types/api-response-interface';
import { LOCAL_STORAGE_DATA_KEYS } from '@Constants/localstorageDataModel';
import { getDataFromLocalStorage } from '@Src/utils/globalUtilities';
import { jwtDecode } from 'jwt-decode';
import { IFinalValues } from '@Src/types/dashboard-interface';
import jsPDF from 'jspdf';
import LifeCareerPdf from '@Pages/life-career-pdf/LifeCareerPdf';
import Test1Pdf from '@Pages/life-career-pdf/components/test-1-pdf/Test1Pdf';
import Test2Pdf from '@Pages/life-career-pdf/components/test-2-pdf/Test2Pdf';
import Test3Pdf from '@Pages/life-career-pdf/components/test-3-pdf/Test3Pdf';
import Loader from '@Components/loader/Loader';

const Dashboard = () => {
    const [finalValues, setFinalValues] = useState<IFinalValues>();
    const [progressValues, setProgressValues] = useState<{ t1: number; t2: number; t3: number }>();
    const [showAllPDfHtml, setShowAllPdfHTml] = useState(false);
    const [showT1PdfHtml, setShowT1PdfHTml] = useState(false);
    const [showT2PDfHtml, setShowT2PdfHTml] = useState(false);
    const [showT3PDfHtml, setShowT3PdfHTml] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [name, setName] = useState('');
    const [showRevisitModal, setShowRevisitModal] = useState(false);
    const [pendingRevisitId, setPendingRevisitId] = useState<number | null>(null);

    const navigate = useNavigate();
    const token = getDataFromLocalStorage(LOCAL_STORAGE_DATA_KEYS.AUTH_KEY);

    const getFinalValues = async () => {
        setShowLoader(true);
        try {
            const response: IAPIResponse = await httpService(
                MODULES_API_MAP.BASE_API_ROUTE,
                GLOBAL_API_ROUTES.FINAL_VALUES
            ).GET();

            if (response.success) {
                console.log('Original Response Data:', response.data);
                let updatedData = { ...response.data };

                ['t1', 't2', 't3'].forEach((key) => {
                    updatedData[key] = [...updatedData[key]].reverse();
                });

                console.log(updatedData);
                setFinalValues(updatedData);
                // setFinalValues(response.data);
            }
        } catch (err) {
            console.log(err);
        }
        setShowLoader(false);
    };

    const getProgress = async () => {
        try {
            const response: IAPIResponse = await httpService(
                MODULES_API_MAP.BASE_API_ROUTE,
                GLOBAL_API_ROUTES.PROGRESS
            ).GET();

            if (response.success) {
                setProgressValues(response.data);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const getRevisit = async (id: number) => {
        setShowLoader(true);
        try {
            const response: IAPIResponse = await httpService(
                MODULES_API_MAP.BASE_API_ROUTE,
                GLOBAL_API_ROUTES.REVISIT
            ).POST({ test_id: id });

            if (response.success) {
                setProgressValues(response.data);
                navigate(`/test/${id}`);
            }
        } catch (err) {
            console.log(err);
        }
        setShowLoader(false);
    };

    const getButtonText = (value: any, testId?: number) => {
        if (value === 3) return 'Revisit';
        if (value === 1 || value === 2) return 'Continue';

        if (testId === 1) return 'Begin Whole Life Values';
        if (testId === 2) return 'Begin Current Career Values';
        if (testId === 3) return 'Begin Ideal Career Values';

        return 'Begin';
    };

    const redirectConditionally = (progressID: number) => {
        const progressMap = { 1: progressValues?.t1, 2: progressValues?.t2, 3: progressValues?.t3 };
        const val = progressMap[progressID as 1 | 2 | 3];

        if (val !== 3) {
            navigate(`/test/${progressID}`);
        } else {
            setPendingRevisitId(progressID);
            setShowRevisitModal(true);
        }
    };
    const handleRevisitChoice = async (mode: 'full' | 'topten') => {
        setShowRevisitModal(false);
        if (!pendingRevisitId) return;

        if (mode === 'topten') {
            navigate(`/test/${pendingRevisitId}?mode=topten`);
            return;
        }

        // Only call revisit API for full section revisit
        setShowLoader(true);
        try {
            const response: IAPIResponse = await httpService(
                MODULES_API_MAP.BASE_API_ROUTE,
                GLOBAL_API_ROUTES.REVISIT
            ).POST({ test_id: pendingRevisitId });

            if (response.success) {
                navigate(`/test/${pendingRevisitId}`);
            }
        } catch (err) {
            console.log(err);
        }
        setShowLoader(false);
    };

    const openPdfInNewTab = (selector: string) => {
        const element = document.querySelector(selector);
        if (!element) return;

        const doc: any = new jsPDF('p', 'px', 'a4');

        const opt = {
            callback: function (jsPdf: any) {
                const blob = jsPdf.output('blob');
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
            },
            autoPaging: 'text',
            html2canvas: {
                allowTaint: true,
                letterRendering: true,
                logging: false,
                scale: 0.4,
            },
        };

        doc.html(element, opt);
    };

    const handleTest1pdf = () => {
        setShowT1PdfHTml(true);
        setTimeout(() => {
            openPdfInNewTab('.test-1');
            setShowT1PdfHTml(false);
        }, 500);
    };

    const handleTest2pdf = () => {
        setShowT2PdfHTml(true);
        setTimeout(() => {
            openPdfInNewTab('.test-2');
            setShowT2PdfHTml(false);
        }, 500);
    };

    const handleTest3pdf = () => {
        setShowT3PdfHTml(true);
        setTimeout(() => {
            openPdfInNewTab('.test-3');
            setShowT3PdfHTml(false);
        }, 500);
    };

    const handleAllPdf = () => {
        setShowAllPdfHTml(true);
        setTimeout(() => {
            openPdfInNewTab('.pdf-downloader');
            setShowAllPdfHTml(false);
        }, 500);
    };

    useEffect(() => {
        if (token) {
            getFinalValues();
            getProgress();
        }
    }, []);

    useEffect(() => {
        if (token) {
            const decodeToken: IToken = jwtDecode(token);
            const currentTime = Math.floor(Date.now() / 1000);

            if (decodeToken.exp < currentTime) {
                navigate('/');
                window.localStorage.clear();
            } else {
                const [localPart] = decodeToken.email.split('@');
                const cleanedLocalPart = localPart.replace(/[^a-zA-Z.]/g, '');
                const nameParts = cleanedLocalPart.split('.');
                const formattedName = nameParts
                    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                    .join(' ');

                setName(formattedName);
            }
        }
    }, [token]);

    return (
        <div className="md:tw-px-[7rem] md:tw-py-4 tw-p-4">
            {showLoader && <Loader />}

            <div className="tw-ml-2.5 tw-mr-35">
                <h4 className="tw-text-primary tw-font-bold tw-text-lg">
                    Life/Career Values Instructions
                </h4>

                <h4 className="tw-text-primary tw-font-bold tw-text-lg">
                    {(progressValues?.t1 !== 3 &&
                        progressValues?.t2 !== 3 &&
                        progressValues?.t3 !== 3) &&
                        'PART 1: YOUR PERSONAL OR "WHOLE LIFE" VALUES'}

                    {(progressValues?.t1 === 3 &&
                        progressValues.t2 !== 3 &&
                        progressValues.t3 !== 3) &&
                        'PART 2: YOUR CURRENT/ MOST RECENT CAREER VALUES'}

                    {(progressValues?.t1 === 3 &&
                        progressValues.t2 === 3 &&
                        progressValues.t3 !== 3) &&
                        'PART 3: YOUR IDEAL CAREER VALUES'}
                </h4>

                <h5 className="tw-text-[#37B048] tw-my-5 tw-font-semibold tw-text-lg">
                    Step 1 - Exploring Values
                </h5>

                <p className="tw-mt-4">
                    To begin, think about your life in total. This includes your career, family,
                    social life, personal time, community, the world. Also think about
                    relationships, happiness, and spirituality; even think about your views of
                    money or success. In other words, think about everything in your whole life.
                </p>

                <p className="tw-mt-4">
                    In Step 1, you will see a list of six values on the page. Review each value,
                    asking yourself if it is important to you or not.{' '}
                    <span className="tw-text-[#37B048] tw-font-bold">
                        You define these values. Don't worry if you don't have an exact definition.
                    </span>{' '}
                    Those that are unimportant may include values that you believe could be
                    important to someone but not so much to you. Click the arrow to move those
                    that are <strong>NOT</strong> important to you to the box on the right,
                    leaving values in the left hand column that are important to you.
                </p>

                <p className="tw-mt-4">
                    You may select as many values as you want. You can move a value to the other
                    box and move it back if you change your mind. You can go back a page to make
                    changes if you want to do so.{' '}
                    <span className="tw-text-[#37B048] tw-font-bold">
                        Take your time but don't debate this in your mind too much. If you are not
                        sure how important a given value is to you, leave it as "Important".
                    </span>{' '}
                    Click on Next when you are ready to move on the next set of values.
                </p>

                <h5 className="tw-text-[#37B048] tw-my-5 tw-font-semibold tw-text-lg">
                    Step 2 - Top Ten Values
                </h5>

                <p className="tw-mt-4">
                    Once you have gone through each page of values, you will be presented with a
                    list of your Important values. From the list of Important values, indicate
                    Your Top Ten by dragging values to the box on the right. These are the values
                    that you believe are the most significant or important to you -- the values
                    that you would fight for if you had to do so.{' '}
                    <span className="tw-text-[#37B048] tw-font-bold">
                        Don't worry about the order you place them in right now, just select the
                        top ten.
                    </span>{' '}
                    You may only pick ten, so if there is another value you really want to add to
                    your top ten, you must remove one to add different one.
                </p>

                <h5 className="tw-text-[#37B048] tw-my-5 tw-font-semibold tw-text-lg">
                    Step 3 - Rank Your Top Ten
                </h5>

                <p className="tw-mt-4">
                    Carefully review your Top Ten values in the box. Think about which one is most
                    important to you and the next most important, and so on. You may reorder the
                    values by selecting and dragging each value up or down.{' '}
                    <span className="tw-text-[#37B048] tw-font-bold">
                        Arrange the values such that your most important value is at the top,
                        number 1, and so on,
                    </span>{' '}
                    thereby creating a rank ordered list of your Top Ten values.
                </p>
            </div>

            <p className="tw-text-3xl tw-font-semibold tw-mt-5">
                {name} -- Your Important Values
            </p>

            {(progressValues?.t1 === 3 &&
                progressValues.t2 === 3 &&
                progressValues.t3 === 3) && (
                    <div className="tw-mt-5">
                        <p className="tw-text-lg">
                            Now you may view your most important or Top Ten Values from your prior
                            visit(s). You may click each one to download a pdf of that list:
                        </p>

                        <div className="tw-grid md:tw-grid-cols-3 tw-gap-5 tw-grid-cols-1 tw-mt-4">
                            <div className="card">
                                <div className="card-header">Whole Life Values</div>
                                <button onClick={handleTest1pdf} className="download-button">
                                    Download Results
                                </button>
                            </div>

                            <div className="card">
                                <div className="card-header">
                                    Current/Most Recent Career Values
                                </div>
                                <button onClick={handleTest2pdf} className="download-button">
                                    Download Results
                                </button>
                            </div>

                            <div className="card">
                                <div className="card-header">Ideal Career Values</div>
                                <button onClick={handleTest3pdf} className="download-button">
                                    Download Results
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            {(progressValues?.t1 === 3 &&
                progressValues.t2 === 3 &&
                progressValues.t3 === 3) && (
                    <div className="tw-mt-5">
                        <p className="tw-text-lg tw-text-center">
                            If you have completed all three of the above sections, you may now view a
                            Results page, which includes each of the above three lists AND instructions
                            on how to explore your results, section by section, comparing your values
                            in each list. You may also download your Results page.
                        </p>

                        <div className="tw-flex tw-justify-center tw-items-center tw-mt-4">
                            <div className="card">
                                <div className="card-header">
                                    Exploring All Three Lists of Your Top Ten Values
                                </div>
                                <button className="download-button" onClick={handleAllPdf}>
                                    Download Results
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            <div className="tw-mt-5">
                <div className="tw-grid md:tw-grid-cols-3 tw-grid-cols-1 tw-gap-3 tw-mt-5">
                    <div className="section">
                        <div className="section-header">Whole Life Values</div>
                        <button
                            onClick={() => redirectConditionally(1)}
                            className="revisit-button"
                        >
                            {getButtonText(progressValues?.t1, 1)} Whole Life Values
                        </button>
                    </div>

                    <div className="section">
                        <div className="section-header">
                            Current/Most Recent Career Important Values
                        </div>
                        <button
                            onClick={() => redirectConditionally(2)}
                            className="revisit-button"
                        >
                            {getButtonText(progressValues?.t2, 2)} Career Important Values
                        </button>
                    </div>

                    <div className="section">
                        <div className="section-header">Ideal Career Values</div>
                        <button
                            onClick={() => redirectConditionally(3)}
                            className="revisit-button"
                        >
                            {getButtonText(progressValues?.t3, 3)} Ideal Career Values
                        </button>
                    </div>
                </div>

                <p className="tw-text-xl tw-mt-9">
                    Exploring all three lists is the most important part of completing this
                    assessment. Please download it and take the time to complete the exercises.
                    Then you may wish to move on to the
                    <b className="tw-text-blue-500 tw-underline tw-cursor-pointer">
                        <Link to="/nextSteps"> Next Steps.</Link>
                    </b>
                </p>

                {showAllPDfHtml && <LifeCareerPdf name={name} finalValuesData={finalValues} />}
                {showT1PdfHtml && <Test1Pdf name={name} finalValues={finalValues} />}
                {showT2PDfHtml && <Test2Pdf name={name} finalValues={finalValues} />}
                {showT3PDfHtml && <Test3Pdf name={name} finalValuesData={finalValues} />}
            </div>
            {showRevisitModal && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-xl tw-p-8 tw-max-w-md tw-w-full tw-mx-4 tw-shadow-xl">
                        <h3 className="tw-text-xl tw-font-bold tw-text-primary tw-mb-2">
                            Revisit Values
                        </h3>
                        <p className="tw-text-gray-600 tw-mb-6">
                            How would you like to revisit this section?
                        </p>
                        <div className="tw-flex tw-flex-col tw-gap-3">
                            <button
                                onClick={() => handleRevisitChoice('full')}
                                className="tw-w-full tw-py-3 tw-px-4 tw-rounded-lg tw-border-2 tw-border-[#37B048] tw-text-[#37B048] tw-font-semibold hover:tw-bg-[#37B048] hover:tw-text-white tw-transition-colors"
                            >
                                Revisit Entire Section
                                <p className="tw-text-sm tw-font-normal tw-mt-1 tw-opacity-80">
                                    Start from the beginning and re-do all steps
                                </p>
                            </button>
                            <button
                                onClick={() => handleRevisitChoice('topten')}
                                className="tw-w-full tw-py-3 tw-px-4 tw-rounded-lg tw-border-2 tw-border-blue-500 tw-text-blue-500 tw-font-semibold hover:tw-bg-blue-500 hover:tw-text-white tw-transition-colors"
                            >
                                Only Revisit Saved Top 10
                                <p className="tw-text-sm tw-font-normal tw-mt-1 tw-opacity-80">
                                    Jump straight to re-ranking your top 10 values
                                </p>
                            </button>
                        </div>
                        <button
                            onClick={() => setShowRevisitModal(false)}
                            className="tw-mt-4 tw-w-full tw-py-2 tw-text-gray-400 hover:tw-text-gray-600 tw-text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;